import {ACCESS_TOKEN, USER_INFO} from '@constants/auth.constant'
import {IUserInfo} from '@interface/auth.interface'
import {AuthService} from '@services/api/Auth.service'
import {disableSplashScreen, enableSplashScreen} from '@services/helper/splashScreen.helper'
import {LocalStorageService} from '@services/utils/localStorage.service'
import {createContext, Dispatch, SetStateAction, useContext, useEffect, useRef, useState} from 'react'
import {isExpiredToken} from 'utility/jwt'
import * as authHelper from '@services/helper/auth.helper'
import {ENV} from 'config/ENV.config'

const IDLE_LOGOUT_MS = 30 * 60 * 1000 // auto-logout after 30 min of no user activity
const LAST_ACTIVITY_KEY = 'lastActivity'

// True when the persisted last-activity timestamp is older than the idle window.
// The timestamp lives in localStorage, so inactivity is honored across full browser/tab
// restarts — an in-memory timer alone would reset on every reload and never expire.
const isIdleExpired = (): boolean => {
  const raw = localStorage.getItem(LAST_ACTIVITY_KEY)
  if (!raw) return false // no record yet — treat an existing session as fresh
  const last = Number(raw)
  if (!last) return false
  return Date.now() - last > IDLE_LOGOUT_MS
}

const markActivity = () => localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()))

type AuthContextProps = {
  isAuthenticated: boolean
  isChecking: boolean
  saveAuth: (token: string | null, uid: string | null, cid: string | null) => void
  userInfo: IUserInfo | null
  setUserInfo: Dispatch<SetStateAction<IUserInfo | null>>
  logout: () => void
}

const isValidToken = () => {
  const accessToken = LocalStorageService.get(ACCESS_TOKEN)
  if (!accessToken) return false
  return !isExpiredToken(accessToken)
}

const initAuthContextPropsState = {
  isAuthenticated: false,
  isChecking: true,
  saveAuth: () => {},
  userInfo: authHelper.getAuth()?.uInfo,
  setUserInfo: () => {},
  logout: () => {},
}

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState)

const useAuth = () => {
  return useContext(AuthContext)
}

const AuthProvider = ({children}) => {
  const initAuth = authHelper.getAuth()
  const [isChecking, setIsChecking] = useState<boolean>(true)
  const [userInfo, setUserInfo] = useState<IUserInfo | null>(initAuth?.uInfo)
  const [isAuthenticated, makeAuthenticated] = useState<boolean>(isValidToken() && !isIdleExpired())

  useEffect(() => {
    if (initAuth?.accessToken && isIdleExpired()) {
      // Session idled out while the app was closed — clear locally and force re-login.
      // Local-only on purpose: see clearLocalAuth.
      clearLocalAuth()
      setIsChecking(false)
    } else if (initAuth?.accessToken) {
      markActivity()
      checkTokenOnline()
    } else setIsChecking(false)
    // eslint-disable-next-line
  }, [])

  // useEffect(() => {
  //   setInterval(() => {
  //     const isValid = isValidToken()
  //     if (!isValid) {
  //       makeAuthenticated(isValid)
  //       logout()
  //     }
  //   }, 5000)
  // }, [])

  // sso answers 402 when the company doesn't hold this app — never subscribed, cancelled, or
  // suspended for an unpaid invoice. Send the user to the SSO portal, where it can be activated.
  // Local auth is dropped WITHOUT calling AuthService.logout(): that revokes the SSO-shared token
  // server-side, which would land them on the portal signed out and unable to subscribe.
  const bounceToPortal = (reason?: string) => {
    authHelper.removeAuth()
    localStorage.removeItem(LAST_ACTIVITY_KEY)
    makeAuthenticated(false)

    let portal = ''
    try {
      portal = new URL(ENV.ssoURL || '').origin
    } catch {
      portal = ''
    }
    if (!portal) return // no usable portal URL configured — leave them on the login screen

    const query = reason ? `?reason=${encodeURIComponent(reason)}` : ''
    window.location.replace(`${portal}/dashboard${query}`)
  }

  const checkTokenOnline = () => {
    setIsChecking(true)
    const appCode = ENV.appCode
    let bouncing = false
    AuthService.verifyToken(appCode)
      .then((resp) => {
        const body = resp?.body || {}
        // sso returns the workspaces under `companylist` (an escaped JSON string);
        // the app consumes `companies` (parsed array) — normalize here.
        let companies = body.companies
        if (!companies) {
          try {
            companies =
              typeof body.companylist === 'string'
                ? JSON.parse(body.companylist)
                : body.companylist || []
          } catch {
            companies = []
          }
        }
        const userInfo = {...body, companies}
        setUserInfo(userInfo)
        LocalStorageService.set(USER_INFO, userInfo)
        makeAuthenticated(true)
      })
      .catch((err) => {
        if (err?.code === 402) {
          bouncing = true
          bounceToPortal(err?.msg)
          return
        }
        // sso rejected this token — it is already invalid, so clear locally rather than
        // firing the global revoke at the user's other live sessions.
        clearLocalAuth()
      })
      // Hold the splash screen while the browser navigates to the portal, so the login
      // screen doesn't flash up in between.
      .finally(() => {
        if (!bouncing) setIsChecking(false)
      })
  }

  const saveAuth = (token: string | null, uid: string | null, cid: string | null) => {
    if (token && uid) {
      authHelper.setAuth(token, uid, cid)
      markActivity()
      checkTokenOnline()
    } else {
      authHelper.removeAuth()
      localStorage.removeItem(LAST_ACTIVITY_KEY)
      makeAuthenticated(false)
    }
  }

  // Drop THIS origin's session only. Used for every automatic expiry — the idle timer and a
  // verify-token rejection — where the token is already dead (or dead to us) and there is
  // nothing left to revoke.
  //
  // It must NOT call AuthService.logout(): that is sso's SINGLE-logout, which revokes every
  // token the user holds, across every app. `lastActivity` is per-origin, so a console tab idling
  // says nothing about the sso portal — and a global revoke from here killed a portal session
  // created seconds earlier, forcing a second login. Nothing is leaked by skipping it: this
  // token expires on its own (sso gives it a 30-min sliding Redis TTL).
  const clearLocalAuth = () => saveAuth(null, null, null)

  const logout = () => {
    // Single logout — the user ASKED to sign out, so revoke the SSO-shared token server-side
    // (storage still holds it so the axios interceptor can attach it), then clear local auth
    // regardless of the call's outcome. Only a deliberate logout belongs here.
    AuthService.logout()
      .catch(() => {})
      .finally(() => clearLocalAuth())
  }

  // Auto-logout after IDLE_LOGOUT_MS of no user activity (mouse/keyboard/scroll/touch).
  // Activity bumps a throttled localStorage timestamp (shared across tabs); a poll checks
  // the elapsed time, so idling logs out even after the app was backgrounded or asleep —
  // and one active tab keeps the others alive.
  const idleExpireRef = useRef(clearLocalAuth)
  useEffect(() => {
    idleExpireRef.current = clearLocalAuth
  })
  const lastBumpRef = useRef(0)
  useEffect(() => {
    if (!isAuthenticated) return
    const bump = () => {
      const now = Date.now()
      if (now - lastBumpRef.current < 5000) return // throttle writes to <=1/5s
      lastBumpRef.current = now
      localStorage.setItem(LAST_ACTIVITY_KEY, String(now))
    }
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'wheel', 'click']
    events.forEach((e) => window.addEventListener(e, bump, {passive: true}))
    markActivity()
    const poll = setInterval(() => {
      if (isIdleExpired()) idleExpireRef.current()
    }, 60 * 1000) // check every minute
    return () => {
      clearInterval(poll)
      events.forEach((e) => window.removeEventListener(e, bump))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated])

  isChecking ? enableSplashScreen() : disableSplashScreen()

  return (
    <AuthContext.Provider
      value={{isAuthenticated, isChecking, saveAuth, userInfo, setUserInfo, logout}}
    >
      {children}
    </AuthContext.Provider>
  )
}

export {AuthProvider, useAuth}
